import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Activity, Eye, Briefcase, LogIn, FileText } from 'lucide-react';
import {
  useAdminData, Metric, LoadingBlock, ErrorBox, SectionNote, count, pct,
} from '../../components/admin/AdminKit';

// WO-36 — only what the app records. Searches, AI summaries, exports, report
// downloads and session length are not recorded yet, so they say so instead
// of showing 0.

const NOT_MEASURED_LABELS = {
  searches: 'Searches',
  ai_summaries: 'AI summaries',
  exports: 'Excel/CSV exports',
  report_downloads: 'Report downloads',
  session_length: 'Time spent per visit',
};

export default function AdminProductUsage() {
  const { data, loading, error, reload } = useAdminData('/api/analytics/product/usage');
  if (loading) return <LoadingBlock testid="admin-usage-loading" />;
  const a = data?.active_users || {};
  const v = data?.page_views_30d || {};
  const crm = data?.crm || {};

  return (
    <div className="space-y-6" data-testid="admin-product-usage">
      <div>
        <h1 className="text-2xl font-bold">Product Usage</h1>
        <p className="text-muted-foreground">What customers do in the app (admins and internal accounts left out)</p>
      </div>
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Active, last 24 h" icon={Activity} value={count(a.dau)} />
            <Metric label="Active, last 7 days" icon={Activity} value={count(a.wau)} />
            <Metric label="Active, last 30 days" icon={Activity} value={count(a.mau)}
              note={a.engagement_rate != null ? `${pct(a.engagement_rate)} of customer users` : null} />
            <Metric label="Sign-ins, last 30 days" icon={LogIn} value={count(data.sign_ins_30d)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-primary" />List pages opened, last 30 days</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['news', 'tenders', 'projects', 'companies', 'reports'].map((p) => (
                  <div key={p} className="p-3 rounded-lg bg-muted/30 text-center">
                    <p className="text-xl font-bold">{count(v[p])}</p>
                    <p className="text-xs text-muted-foreground capitalize">{p}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-primary" />CRM</CardTitle>
                <CardDescription>Everything customers have saved so far</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  ['Opportunities', crm.opportunities_created], ['Contacts', crm.contacts_created],
                  ['From tenders', crm.from_tenders], ['From projects', crm.from_projects],
                  ['From companies', crm.from_companies],
                ].map(([label, n]) => (
                  <div key={label} className="p-3 rounded-lg bg-muted/30 text-center">
                    <p className="text-xl font-bold">{count(n)}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Metric label="Reports purchased" icon={FileText} value={count(data.reports_purchased)} />
            {(data.not_measured || []).map((key) => (
              <Metric key={key} label={NOT_MEASURED_LABELS[key] || key} value={null}
                why="The app does not record this yet (Phase 2 tracking plan)" />
            ))}
          </div>
          <SectionNote>
            "Active" means the person used the app in that period (recorded from 22/09/2026); before that date the
            only record is the password sign-in.
          </SectionNote>
        </>
      )}
    </div>
  );
}
